import deepmerge from 'deepmerge'
import { v4 as uuidv4 } from 'uuid'

import axios from '@/plugins/axios'

import { cleanQueryKey, queryKeyToUrl, getIdFieldName, getBaseQueryKey } from './query_utils.ts'
import queryClient from './query_client.js'
import dconsole from '@/plugins/debug_console'

// TODO: migrate optimistic updates to variables-based approach (requires refactoring calling components):
// https://tanstack.com/query/latest/docs/framework/vue/guides/optimistic-updates

const defaultOnMutate = async (variables = {}) => {
  // Nothing to do for pessimistic mutations:
  if (!variables.optimistic) return {}

  const {
    verb,
    queryKey: rawQueryKey,
    listQueryKey,
    baseQueryKey: baseQueryKeyRaw,
    rowId,
    data,
    mergeOptions,
  } = variables
  const queryKey = cleanQueryKey(rawQueryKey)
  const baseQueryKey = baseQueryKeyRaw ?? getBaseQueryKey(queryKey, verb)

  dconsole.debug(`🏁 defaultOnMutate: verb: ${verb}, queryKey: ${queryKey}`)

  const context = { previousData: queryClient.getQueryData(queryKey) }
  const idFieldName = getIdFieldName(queryKey)

  // Optimistic updates:
  if (verb == 'POST' && listQueryKey) {
    // Insert query with a transient id
    const tempId = `temp-${uuidv4()}`
    await queryClient.setQueryData(listQueryKey, (cachedData) => [
      ...(cachedData || []),
      { [idFieldName]: tempId, ...data, tempClient: true },
    ])
    context.createdItem = { [idFieldName]: tempId }
  } else if (verb == 'DELETE' && listQueryKey && rowId) {
    await queryClient.setQueryData(listQueryKey, (cachedData) =>
      (cachedData || []).filter((item) => item[idFieldName] !== rowId)
    )
  } else if (['PATCH', 'PUT'].includes(verb)) {
    if (baseQueryKey) {
      // Avoid race condition on multiple quick successive edits:
      dconsole.debug(`🔧 defaultOnMutate: cancelling: ${baseQueryKey}`)
      await queryClient.cancelQueries({ queryKey: baseQueryKey, exact: true })
      queryClient.setQueriesData({ queryKey: baseQueryKey, exact: true }, (cachedData) => {
        if (!cachedData) return
        return verb == 'PUT' ? data : deepmerge(cachedData, data, mergeOptions ?? {})
      })
    }

    if (listQueryKey && rowId) {
      queryClient.setQueriesData({ queryKey: listQueryKey, exact: true }, (cachedData) => {
        return (cachedData || []).map((item) => {
          if (item[idFieldName] !== rowId) return item
          return verb == 'PUT' ? data : deepmerge(item, data, mergeOptions ?? {})
        })
      })
    }
  }

  return context
}

const defaultMutationFn = async ({ verb, queryKey, data, throttle, axiosOptions = {} }) => {
  queryKey = cleanQueryKey(queryKey)

  dconsole.debug(`🏁 defaultMutationFn: verb: ${verb}, queryKey: ${queryKey}`)

  const { url, params } = queryKeyToUrl(queryKey)
  if (throttle) {
    const throttleDuration = throttle === true ? 1000 : throttle
    dconsole.debug(`⏳ Throttling mutation ${queryKey} by ${throttleDuration}ms`)
    await new Promise((resolve) => setTimeout(resolve, throttleDuration))
  }
  const { data: returnedData } = await axios({
    method: verb,
    url,
    params,
    data,
    ...axiosOptions,
  })

  return returnedData
}

const defaultOnSuccess = async (data, variables, context) => {
  const {
    verb,
    queryKey: queryKeyRaw,
    listQueryKey,
    onSuccess,
    updateCacheOnSuccess,
    returnsFullResult,
  } = variables
  const queryKey = cleanQueryKey(queryKeyRaw)
  const idFieldName = getIdFieldName(queryKey)

  dconsole.debug(
    `🏁 defaultOnSuccess: verb: ${verb}, queryKey: ${queryKey} | context: ${Object.keys(context)}`
  )

  // Updating cache on PATCH/PUT will potentially cause race issues (eg. if user does multiple quick edits)
  // updateCacheOnSuccess defaults to false for PATCH/PUT to avoid this
  // additionally returnsFullResult can be set to true if the endpoint returns the full updated object

  // NOTE: for optimistic mutations (or if forceRefetch is set), the cache will be updated through invalidation in onSettled

  if (['PATCH', 'PUT'].includes(verb) && updateCacheOnSuccess !== false) {
    dconsole.debug(`💾 defaultOnSuccess: updating cache for ${queryKey}`)
    await queryClient.setQueryData(queryKey, (cachedData) => {
      if (returnsFullResult) return data
      else return deepmerge(cachedData || {}, data)
    })
  }

  // Update list cache if needed (remove optimistic item if applicable):
  if (verb == 'POST' && listQueryKey && updateCacheOnSuccess !== false) {
    await queryClient.setQueryData(listQueryKey, (cachedData) => {
      if (!cachedData) return cachedData
      const newData = Array.isArray(data)
        ? data.map((item) => ({ ...item, tempClient: false }))
        : [{ ...data, tempClient: false }]
      return [
        ...cachedData.filter(
          (item) => !context.createdItem || item.idFieldName !== context.createdItem[idFieldName]
        ),
        ...newData,
      ]
    })
  }

  if (onSuccess) {
    await onSuccess(data, variables, context)
  }
}

const defaultOnSettled = async (_data, error, variables, context) => {
  const {
    verb,
    optimistic = true,
    forceRefetch = true,
    queryKey: queryKeyRaw,
    baseQueryKey: baseQueryKeyRaw,
    listQueryKey,
  } = variables
  const queryKey = cleanQueryKey(queryKeyRaw)
  const baseQueryKey = baseQueryKeyRaw ?? getBaseQueryKey(queryKey, verb)

  dconsole.debug(`🏁 defaultOnSettled: queryKey: ${queryKey} (error: ${error})`)

  // Prevent race condition on multiple quick successive edits:
  await queryClient.cancelQueries({ queryKey: baseQueryKey, exact: true })

  if (error) {
    dconsole.warn(`⚠️ defaultOnSettled: mutation error: ${error.message}`)
    if (optimistic && ['PATCH', 'PUT'].includes(verb)) {
      // Rollback optimistic update:
      await queryClient.setQueryData(baseQueryKey, context.previousData)
      //NOTE: POST/DELETE will be handled through query invalidation below
    }
  }

  // If there are 2 or more mutations (including current), do not invalidate yet:
  let mutatingQueries = queryClient.isMutating({ queryKey, exact: true })

  if (mutatingQueries < 2 && forceRefetch) {
    dconsole.debug(`🔧 defaultOnSettled: invalidated queryKey: ${baseQueryKey}`)
    await queryClient.invalidateQueries({ queryKey: baseQueryKey, exact: true })
    if (listQueryKey && baseQueryKey !== listQueryKey) {
      dconsole.debug(`🔧 defaultOnSettled: invalidated listQueryKey: ${listQueryKey}`)
      await queryClient.invalidateQueries({ queryKey: listQueryKey, exact: true })
    }
  }

  dconsole.debug(`✅ defaultOnSettled: queryKey: ${queryKey}`)
}

export { defaultMutationFn, defaultOnMutate, defaultOnSuccess, defaultOnSettled }
